import { Box, Button, Paper, Typography } from '@mui/material';
import { useAppSelector } from 'redux/store-hooks';
import { selectJustInTimeCalls } from 'redux/slices/callsSlice';
import { useMobile } from '@act/utilities/mobile-context';
import { IconLibrary } from '@act/storybook';
import { formatPhoneNumber } from '@act/shared/utilities';
import { CallDto } from '@act/shared/data-transfer-objects';
import { useMemo, useState } from 'react';
import { utilityAppSelectors } from 'redux/utils/selectors/utility-selectors';
import { displayName } from 'utilities/formatters/display-name';
import { useInteractionsDialog } from '../dialog-forms/hooks/use-interactions-dialog';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { ActiveCallMenu } from '../member-page/Calls/ActiveCallMenu';
import { actingUserAppSelectors } from 'redux/redux';

interface JustInTimeCallCardProps {
  call: CallDto;
}

const JustInTimeCallCard = ({ call }: JustInTimeCallCardProps) => {
  const { isMobile } = useMobile();
  const threeWayCallsFF = useFeatureFlag('three-way-calls');
  const threeWayKnownCallerFF = useFeatureFlag('three-way-known-caller');
  const interactionsDialog = useInteractionsDialog();
  const currentUser = actingUserAppSelectors.useSelectActingUser();
  const caller = utilityAppSelectors.selectUserOrMemberById(
    call?.originator?.userId
  );
  const receiver = utilityAppSelectors.selectUserOrMemberById(
    call?.receiver?.userId
  );
  // Always call the hook, but pass null/undefined when no subjectId
  // Fallback to subjectId if caller/receiver not found (e.g., after call assignment)
  const subjectMember = utilityAppSelectors.selectUserOrMemberById(
    call?.subjectId || null
  );

  const [openThreeWayCallsMenu, setOpenThreeWayCallsMenu] = useState(false);

  const handleOpenCall = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Check if the current guide is assigned to the call subject
    const isAssignedToCurrentGuide =
      callSubject?.assignedGuideId === currentUser?.id;

    if (isAssignedToCurrentGuide && callSubject) {
      // Current guide is assigned - open full screen call display
      interactionsDialog.editCall({ call, user: callSubject });
    } else {
      // Current guide is not assigned - open ActiveCallMenu
      setOpenThreeWayCallsMenu(!openThreeWayCallsMenu);
    }
  };

  // Use caller/receiver if available, otherwise fallback to subjectMember
  const callSubject = call.incoming
    ? caller || subjectMember
    : receiver || subjectMember;

  const phoneNumber =
    call.originator?.phoneNumber || call.receiver?.phoneNumber;

  // Figure out if this call was an invite from a guide,
  // get the call connection where the current user is the connected user.
  const currentUserCallConnection = call.connections.find(
    connection => connection.userId === currentUser?.id
  );
  // See if it was from an invite.
  const inviteLog = currentUserCallConnection?.connectionLogs.find(
    log => log.event === 'invite'
  );
  const inviteGuide = inviteLog?.actingUser
    ? displayName(inviteLog.actingUser)
    : null;

  const callDisplayName = callSubject ? displayName(callSubject) : 'Unknown';

  if (!threeWayCallsFF) {
    return null;
  }

  // Hide known caller calls if feature flag is off AND the call was NOT originally unknown
  // wasOriginallyUnknown is set at call creation and never changes, so even if a call
  // gets assigned to a member later, we'll still show it if it started as unknown
  if (!threeWayKnownCallerFF && !call.wasOriginallyUnknown) {
    return null;
  }

  // Check if current user's connection is still active (not completed)
  const currentUserConnection = call.connections?.find(
    conn => conn.userId === currentUser?.id
  );
  const isCurrentUserStillOnCall =
    currentUserConnection && !currentUserConnection.completedAt;

  // Hide the call if current user's connection is closed/completed
  if (!isCurrentUserStillOnCall) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: '12px 16px',
        marginBottom: '12px',
        minWidth: isMobile ? '320px' : '400px',
        maxWidth: isMobile ? '90vw' : '500px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            flex: 1,
            minWidth: 0,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#E8F5F3',
              flexShrink: 0,
            }}
          >
            <IconLibrary
              iconName="Phone"
              size={20}
              weight="regular"
              color="#00796B"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {inviteLog && inviteGuide ? (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Invite to join from {inviteGuide}
              </Typography>
            ) : null}
            <Typography
              variant="titleMedium"
              color="text.primary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
            >
              {callDisplayName}
            </Typography>
            {phoneNumber && (
              <Typography
                variant="bodyMedium"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatPhoneNumber(phoneNumber)}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpenCall}
          startIcon={
            <IconLibrary iconName="Phone" weight="regular" size={18} />
          }
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            padding: '8px 20px',
          }}
        >
          {openThreeWayCallsMenu ? 'Close call' : 'Open call'}
        </Button>
      </Box>
      {openThreeWayCallsMenu && (
        <Box
          sx={{
            marginTop: '16px',
            width: '100%',
          }}
        >
          <ActiveCallMenu call={call} />
        </Box>
      )}
    </Paper>
  );
};

export const JustInTimeCallDisplay = () => {
  const justInTimeCalls = useAppSelector(selectJustInTimeCalls);
  const { isMobile } = useMobile();

  const sortedCalls = useMemo(() => {
    return [...justInTimeCalls].sort((a, b) => {
      const timeA = a.timestamp || a.timeStarted || 0;
      const timeB = b.timestamp || b.timeStarted || 0;
      return timeB - timeA; // Most recent first
    });
  }, [justInTimeCalls]);

  if (sortedCalls.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: isMobile ? '80px' : '100px',
        right: isMobile ? '8px' : '16px',
        zIndex: 1300, // Above most things but below modals
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        overflowX: 'visible',
        padding: '8px', // Add padding to prevent shadow clipping
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
        },
      }}
    >
      {sortedCalls.map(call => (
        <JustInTimeCallCard key={call.id} call={call} />
      ))}
    </Box>
  );
};
